import React, { useEffect, useState, useRef } from 'react';
import { Upload, Modal, message, Select, Col, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { baseURL, getImageFallbackUrl } from '../../utils/helpers';

const { Option } = Select;

const ImagesUploader = ({
  initialImages = [],
  fileList,
  setFileList,
  deletedImages,
  setDeletedImages,
  onValidationChange,
}) => {
  const [previewImage, setPreviewImage] = useState(''); // State for preview image
  const [previewVisible, setPreviewVisible] = useState(false); // State to control preview modal
  const hasLoadedInitialImages = useRef(false); // Track if initial images have been loaded
  const [uploadError, setUploadError] = useState('');
  const [failedPreviewImages, setFailedPreviewImages] = useState(new Set()); // Track failed preview images
  const [failedThumbnailImages, setFailedThumbnailImages] = useState(new Set()); // Track failed thumbnail images

  // Handle thumbnail image error - try fallback URL
  const handleThumbnailImageError = (imageUrl, imgElement) => {
    if (!failedThumbnailImages.has(imageUrl)) {
      setFailedThumbnailImages(prev => new Set([...prev, imageUrl]));
      // Update the image src to use fallback URL
      if (imgElement) {
        imgElement.src = getImageFallbackUrl(imageUrl);
      }
    }
  };

  // Add error handlers to thumbnail images using event delegation
  useEffect(() => {
    const handleImageError = (e) => {
      const img = e.target;
      if (img.classList.contains('ant-upload-list-item-image') && !img.hasAttribute('data-error-handled')) {
        img.setAttribute('data-error-handled', 'true');
        const originalSrc = img.src;
        handleThumbnailImageError(originalSrc, img);
      }
    };

    // Use event delegation on the document to catch all thumbnail image errors
    document.addEventListener('error', handleImageError, true);

    return () => {
      document.removeEventListener('error', handleImageError, true);
    };
  }, [fileList]); // Re-run when fileList changes

  // On component mount, load initial images into the fileList state
  useEffect(() => {
    if (!hasLoadedInitialImages.current && initialImages.length > 0) {
      // Only set fileList if it's the first render and there are initial images
      setFileList(initialImages);
      hasLoadedInitialImages.current = true; // Mark initial images as loaded
    }
  }, [initialImages, setFileList]);

  // Handle file change (Upload, Update, Delete)
  const handleChange = ({ fileList: newFileList }) => {
    // Ant Design's Upload component normalizes file objects, which can strip custom properties
    // (like originFileObj, isStaged, linkedImageFile) that we added for staged images.
    // We need to preserve these properties by merging with the existing fileList.
    const mergedFileList = newFileList.map(newFile => {
      // Find the existing file with the same uid
      const existingFile = fileList.find(f => f.uid === newFile.uid);
      
      if (existingFile) {
        // Merge the new file with the existing file, preserving custom properties
        return {
          ...existingFile,  // Keep custom properties from existing file
          ...newFile,       // Apply updates from Ant Design
          // Explicitly preserve critical properties that might be overwritten
          originFileObj: existingFile.originFileObj || newFile.originFileObj,
          isStaged: existingFile.isStaged !== undefined ? existingFile.isStaged : newFile.isStaged,
          linkedImageFile: existingFile.linkedImageFile || newFile.linkedImageFile,
          linkedImageId: existingFile.linkedImageId || newFile.linkedImageId,
          image_url: existingFile.image_url || newFile.image_url // Preserve CDN URL
        };
      }
      
      // New file (not in existing list), use as-is
      return newFile;
    });

    // Update the file list - validation is handled in beforeUpload
    setFileList(mergedFileList);
  };

  // Validate file before adding to list
  const validateFile = file => {
    console.log('ImagesUploader validateFile called for:', file.name, 'Size:', file.size, 'Type:', file.type); // Debug log

    const MAX_SIZE_BYTES = 0.75 * 1024 * 1024; // 0.75 MB
    const hasImageMimeType = file.type && file.type.startsWith('image/');
    const hasImageExtension = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
    const isImage = hasImageMimeType || hasImageExtension;

    console.log('ImagesUploader validation checks:', { hasImageMimeType, hasImageExtension, isImage, fileSize: file.size, maxSize: MAX_SIZE_BYTES }); // Debug log

    if (!isImage) {
      const errorText = 'Only image files are allowed (jpg, png, gif, webp, bmp, svg).';
      console.log('ImagesUploader setting error message:', errorText); // Debug log
      setUploadError(errorText);
      onValidationChange && onValidationChange(false);
      return false;
    }

    if (typeof file.size === 'number' && file.size > MAX_SIZE_BYTES) {
      const errorText = `Each image must be 0.75 MB or smaller. Current size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`;
      console.log('ImagesUploader setting error message:', errorText); // Debug log
      setUploadError(errorText);
      onValidationChange && onValidationChange(false);
      return false;
    }

    // File is valid - clear error message
    console.log('ImagesUploader file validation passed, clearing error message'); // Debug log
    setUploadError('');
    onValidationChange && onValidationChange(true);
    return true;
  };

  // Handle file upload before adding it to the list
  const beforeUpload = file => {
    console.log('ImagesUploader beforeUpload called for:', file.name, 'Size:', file.size, 'Type:', file.type); // Debug log

    // Validate file immediately when selected
    const isValid = validateFile(file);

    if (!isValid) {
      // File is invalid - prevent it from being added to the list
      console.log('ImagesUploader file rejected in beforeUpload - invalid'); // Debug log
      return Upload.LIST_IGNORE;
    }

    // File is valid - allow it to be processed by handleChange
    console.log('ImagesUploader file accepted in beforeUpload - valid'); // Debug log
    return false; // Prevent auto-upload but allow file to be added to list
  };

  // Handle preview modal visibility
  const handlePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj); // Convert to base64 for preview
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  // Close the preview modal
  const handleCancel = () => setPreviewVisible(false);

  // Handle preview image error - try fallback URL
  const handlePreviewImageError = (imageUrl) => {
    if (!failedPreviewImages.has(imageUrl)) {
      setFailedPreviewImages(prev => new Set([...prev, imageUrl]));
      // Update the preview image to use fallback URL
      setPreviewImage(prev => prev === imageUrl ? getImageFallbackUrl(imageUrl) : prev);
    }
  };

  // Helper function to convert image to Base64
  const getBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image removal
  const handleRemove = file => {
    if (file.id || file.url) {
      // This is an existing image, move it to deletedImages array
      setDeletedImages(prev => [...prev, file]);
    }
    // Remove the image from the displayed list
    setFileList(fileList.filter(item => item.uid !== file.uid));
    message.success('Image removed');
  };

  // Custom request to prevent default upload behavior
  const customRequest = ({ file, onSuccess }) => {
    // Simulate successful upload without actually uploading
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  return (
    <div>
      <Upload
        multiple // Allow selecting multiple images at once
        listType="picture-card" // Allows displaying thumbnails of uploaded images
        fileList={fileList} // Controlled by parent
        onPreview={handlePreview} // Preview the image
        onChange={handleChange} // Update the file list when images are uploaded or removed
        beforeUpload={beforeUpload} // Validate file before adding to list
        onRemove={handleRemove} // Handle file removal
        customRequest={customRequest}
        accept="image/*"
      >
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      </Upload>
      {uploadError ? (
        <div style={{ color: '#ff4d4f', marginTop: 8 }}>{uploadError}</div>
      ) : null}

      {/* Modal to preview image */}
      <Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
        <img
          alt="example"
          style={{ width: '100%' }}
          src={previewImage}
          onError={() => handlePreviewImageError(previewImage)}
        />
      </Modal>
    </div>
  );
};

export default ImagesUploader;
